import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import {
  computeLocationInventoryStats,
  formatInventoryValueEur,
  type LocationInventoryStatsResult,
} from "../utils/computeLocationInventoryStats";
import { itemsWithUnknownCostNote, partlyKnownCost } from "../utils/unknownCost";
import { CostNote } from "./ui/CostNote";
import { Modal } from "./Modal";

const NO_LOCATION_IDS: string[] = [];

export type InventoryStatisticsModalProps =
  | {
      open: boolean;
      onClose: () => void;
      /** One shop (location dashboard). */
      scope: "location";
      locationId: string;
      locationName: string;
    }
    | {
      open: boolean;
      onClose: () => void;
      /** Combined stats on the home page: all active shops for owners, assigned shop only for manager/florist. */
      scope: "allLocations";
      locationIds: string[];
      /** Modal title suffix after middle dot (e.g. All shops or one shop name). */
      aggregateLabel: string;
    };

export function InventoryStatisticsModal(props: InventoryStatisticsModalProps) {
  const { open, onClose } = props;
  const scope = props.scope;

  const locationId = scope === "location" ? props.locationId : "";
  const locationName = scope === "location" ? props.locationName : "";
  const locationIds = scope === "allLocations" ? props.locationIds : NO_LOCATION_IDS;
  const aggregateLabel = scope === "allLocations" ? props.aggregateLabel : "";

  const title =
    scope === "location"
      ? `Inventory statistics · ${locationName}`
      : `Inventory statistics · ${aggregateLabel}`;

  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsResult, setStatsResult] = useState<LocationInventoryStatsResult | null>(null);

  const loadInventoryStats = useCallback(async () => {
    if (scope === "location") {
      if (!locationId) return;
    } else if (locationIds.length === 0) {
      return;
    }

    setStatsLoading(true);
    setStatsError(null);
    try {
      const cats = await api.listCategories();
      const sorted = [...cats].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      );
      const labelByCode = new Map(sorted.map((c) => [c.code, c.label]));

      let rows;
      if (scope === "location") {
        rows = await api.getBalance({ locationId });
      } else {
        const parts = await Promise.all(
          locationIds.map((id) => api.getBalance({ locationId: id })),
        );
        rows = parts.flat();
      }

      setStatsResult(computeLocationInventoryStats(rows, labelByCode));
    } catch (err: unknown) {
      setStatsError(err instanceof Error ? err.message : "Could not load statistics");
      setStatsResult(null);
    } finally {
      setStatsLoading(false);
    }
  }, [scope, locationId, locationIds]);

  useEffect(() => {
    if (!open) return;
    if (scope === "location" && !locationId) return;
    if (scope === "allLocations" && locationIds.length === 0) return;
    void loadInventoryStats();
  }, [open, scope, locationId, locationIds, loadInventoryStats]);

  useEffect(() => {
    if (!open) {
      setStatsError(null);
      setStatsResult(null);
    }
  }, [open]);

  const introPrimary = useMemo(() => {
    if (scope === "location") {
      return "Figures cover this entire shop (all zones and categories). Filters on the page above do not apply.";
    }
    if (locationIds.length <= 1) {
      return "Figures cover this shop (all zones and categories).";
    }
    return "Figures combine every active shop (all zones and categories). Items stocked at multiple shops are counted once by catalog item with quantities and values summed.";
  }, [scope, locationIds.length]);

  if (!open) return null;

  return (
    <Modal
      title={title}
      onClose={() => {
        onClose();
        setStatsError(null);
        setStatsResult(null);
      }}
    >
      <div className="inventory-stats-modal">
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          {introPrimary}
        </p>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Values use each row&apos;s average purchase unit cost where recorded. Flowers and many perishables have no unit cost here — totals may undercount real stock value.
        </p>
        {statsLoading ? (
          <p className="muted" style={{ margin: "var(--space-3) 0 0" }}>
            Loading…
          </p>
        ) : null}
        {statsError ? (
          <div style={{ marginTop: "var(--space-3)" }}>
            <div className="alert error">{statsError}</div>
            <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
              <button type="button" className="button" onClick={() => void loadInventoryStats()}>
                Retry
              </button>
            </div>
          </div>
        ) : null}
        {!statsLoading && !statsError && statsResult ? (
          <>
            <div className="inventory-stats-modal-summary">
              <div>
                <span className="inventory-stats-modal-summary-label">Distinct items</span>
                <strong className="inventory-stats-modal-summary-value">
                  {statsResult.totalDistinctItems}
                </strong>
              </div>
              <div>
                <span className="inventory-stats-modal-summary-label">Total value (known cost)</span>
                <strong className="inventory-stats-modal-summary-value">
                  {formatInventoryValueEur(statsResult.totalValueEur)}
                </strong>
                {/* WO-B part 6 (OD-198): how many items that value leaves part of out. */}
                <CostNote note={itemsWithUnknownCostNote(statsResult.unknownCostItemCount)} />
              </div>
            </div>
            {statsResult.byCategory.length === 0 ? (
              <p className="muted" style={{ marginTop: "var(--space-3)" }}>
                {scope === "location"
                  ? "No on-hand stock at this location."
                  : locationIds.length <= 1
                    ? "No on-hand stock at this shop."
                    : "No on-hand stock at any active shop."}
              </p>
            ) : (
              <div
                className="table-scroll inventory-stats-modal-scroll"
                role="region"
                aria-label="Inventory statistics by category"
                tabIndex={0}
              >
                <table className="table inventory-stats-table">
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Distinct items</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsResult.byCategory.map((row) => (
                      <tr key={row.categoryCode}>
                        <td>{row.categoryLabel}</td>
                        <td>{row.distinctItems}</td>
                        <td>
                          {partlyKnownCost(
                            row.valueEur,
                            row.unknownCostItemCount > 0,
                            formatInventoryValueEur,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
